/* React Query rond de datalaag van de projectpagina. De client is de gewone
 * CRM-client (sessie van de ingelogde gebruiker); de datalaag zelf krijgt hem als
 * argument, zodat de toetsen hem kunnen nabootsen. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  haalGekoppeld, haalLogboek, haalOffertes, haalProject, maakOfferte, rondTaakAf, voegMijlpaalToe, voegNotitieToe, voegTaakToe, wijzigProject,
  type PaginaClient, type PaginaProject, type ProjectWijziging,
} from './data/project';
import { haalBestanden, uploadBestand, type Bestand, type BestandenClient, type UploadVerzoek } from './data/bestanden';
import { haalMonitor, type MonitorClient } from './data/monitor';
import {
  haalBestandsinhoud, haalFeedback, haalMensen, haalPuntWeg, klaarMetFeedback, plaatsPunt, reageer, vraagFeedback, zetStatus,
  type Anker, type FeedbackClient, type FeedbackStatus, type VersieRef,
} from './data/feedback';

/* De gegenereerde types kennen ems en de tabellen van na types.ts niet. */
const client = supabase as unknown as PaginaClient & BestandenClient & MonitorClient & FeedbackClient;

export const paginaSleutel = (id: string | undefined) => ['projectpagina', id] as const;

export function usePaginaProject(id: string | undefined) {
  return useQuery({
    queryKey: [...paginaSleutel(id), 'project'],
    queryFn: () => haalProject(client, id!),
    enabled: Boolean(id),
  });
}

export function useWijzigProject(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (w: ProjectWijziging) => wijzigProject(client, id, w),
    onMutate: async (w) => {
      const sleutel = [...paginaSleutel(id), 'project'];
      await qc.cancelQueries({ queryKey: sleutel });
      const vorig = qc.getQueryData<PaginaProject | null>(sleutel);
      if (vorig) qc.setQueryData(sleutel, { ...vorig, ...w });
      return { vorig };
    },
    onError: (_e, _w, ctx) => {
      if (ctx?.vorig) qc.setQueryData([...paginaSleutel(id), 'project'], ctx.vorig);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: [...paginaSleutel(id), 'project'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useMonitor(id: string | undefined) {
  return useQuery({
    queryKey: [...paginaSleutel(id), 'monitor'],
    queryFn: () => haalMonitor(client, id!),
    enabled: Boolean(id),
    staleTime: 60_000,
    /* In de pas met de ophaaldienst (elke 5 minuten). */
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

export function useOffertes(id: string | undefined) {
  return useQuery({ queryKey: [...paginaSleutel(id), 'offertes'], queryFn: () => haalOffertes(client, id!), enabled: Boolean(id) });
}

export function useMaakOfferte() {
  return useMutation({ mutationFn: (p: PaginaProject) => maakOfferte(client, p) });
}

export function useGekoppeld(id: string | undefined) {
  return useQuery({ queryKey: [...paginaSleutel(id), 'gekoppeld'], queryFn: () => haalGekoppeld(client, id!), enabled: Boolean(id) });
}

export function useBestanden(id: string | undefined, projectMap: string | null | undefined) {
  return useQuery({
    queryKey: [...paginaSleutel(id), 'bestanden'],
    queryFn: () => haalBestanden(client, id!, projectMap ?? null),
    enabled: Boolean(id) && projectMap !== undefined,
    staleTime: 30_000,
    retry: false,
  });
}

export function useUpload(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: UploadVerzoek) => uploadBestand(client, v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...paginaSleutel(id), 'bestanden'] });
      qc.invalidateQueries({ queryKey: [...paginaSleutel(id), 'logboek'] });
    },
  });
}

export function useLogboek(id: string | undefined) {
  return useQuery({ queryKey: [...paginaSleutel(id), 'logboek'], queryFn: () => haalLogboek(client, id!), enabled: Boolean(id) });
}

export function useLogboekActies(id: string) {
  const qc = useQueryClient();
  const vernieuw = () => qc.invalidateQueries({ queryKey: [...paginaSleutel(id), 'logboek'] });
  return {
    notitie: useMutation({ mutationFn: (tekst: string) => voegNotitieToe(client, id, tekst), onSuccess: vernieuw }),
    taak: useMutation({ mutationFn: (titel: string) => voegTaakToe(client, id, titel), onSuccess: vernieuw }),
    mijlpaal: useMutation({
      mutationFn: ({ titel, datum }: { titel: string; datum: string }) =>
        voegMijlpaalToe(client, id, titel, datum, new Date().toISOString().slice(0, 10)),
      onSuccess: vernieuw,
    }),
    afronden: useMutation({ mutationFn: (taakId: string) => rondTaakAf(client, taakId), onSuccess: vernieuw }),
  };
}

/* ─── Feedback op documenten (ADR 0021) ─── */

export function useFeedback(id: string | undefined) {
  return useQuery({
    queryKey: [...paginaSleutel(id), 'feedback'],
    queryFn: () => haalFeedback(client, id!),
    enabled: Boolean(id),
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

/** Interne teamleden (voor "Vraag feedback"), met of zonder aangemelde iPhone. */
export function useMensen(enabled = true) {
  return useQuery({ queryKey: ['feedback-mensen'], queryFn: () => haalMensen(client), enabled, staleTime: 5 * 60_000 });
}

export function useFeedbackActies(id: string) {
  const qc = useQueryClient();
  const vernieuw = () => qc.invalidateQueries({ queryKey: [...paginaSleutel(id), 'feedback'] });
  return {
    plaats: useMutation({
      mutationFn: (v: { versie: VersieRef; tekst: string; anker: Anker; verzoekId?: string | null }) => plaatsPunt(client, { projectId: id, ...v }),
      onSuccess: vernieuw,
    }),
    reageer: useMutation({ mutationFn: ({ puntId, tekst }: { puntId: string; tekst: string }) => reageer(client, puntId, tekst), onSuccess: vernieuw }),
    status: useMutation({
      mutationFn: ({ puntId, status, notitie, versie }: { puntId: string; status: FeedbackStatus; notitie?: string; versie?: VersieRef | null }) =>
        zetStatus(client, puntId, status, { notitie, versie }),
      onSuccess: vernieuw,
    }),
    weg: useMutation({ mutationFn: (puntId: string) => haalPuntWeg(client, puntId), onSuccess: vernieuw }),
    klaar: useMutation({
      mutationFn: (verzoekId: string) => klaarMetFeedback(client, verzoekId),
      onSuccess: () => {
        vernieuw();
        qc.invalidateQueries({ queryKey: [...paginaSleutel(id), 'logboek'] });
      },
    }),
  };
}

export function useVraagFeedback(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { versie: VersieRef; titel: string; vraag: string; deadline: string | null; personen: string[] }) =>
      vraagFeedback(client, { projectId: id, ...v }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: [...paginaSleutel(id), 'feedback'] });
      qc.invalidateQueries({ queryKey: [...paginaSleutel(id), 'logboek'] });
    },
  });
}

/** Een Versie verandert nooit (een nieuwe versie is een nieuw bestand), dus de inhoud mag lang blijven. */
export function useBestandsinhoud(projectId: string, bestand: Bestand | null) {
  return useQuery({
    queryKey: ['bestandsinhoud', projectId, bestand?.id],
    queryFn: () => haalBestandsinhoud(client, projectId, bestand!),
    enabled: Boolean(bestand),
    staleTime: Infinity,
    gcTime: 10 * 60_000,
    retry: false,
  });
}
